/**
 * Dashboard API client – fetches data for the Home page.
 *
 * Endpoints:
 *   GET /api/v1/dashboard/summary?project_id=...
 *   GET /api/v1/dashboard/events?project_id=...&limit=5
 *   GET /api/v1/dashboard/actions?project_id=...
 */

import { API_BASE_URL } from '../config';
import { authFetch } from './config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DashboardHealthStatus {
  status: 'ok' | 'warning' | 'critical';
  total_activities: number;
  critical_count: number;
  near_critical_count: number;
}

export interface DashboardBaselineInfo {
  has_baseline: boolean;
  baseline_name?: string;
  baseline_date?: string;
  baseline_id?: string;
}

export interface DashboardProjectSummary {
  mode: 'project';
  project_name: string;
  empty?: boolean;
  message?: string;
  health: DashboardHealthStatus;
  critical_near_critical: { critical_count: number; near_critical_count: number };
  milestones: { total: number; at_risk: number; overdue: number };
  quality: {
    status: 'pass' | 'warning' | 'fail';
    pass_count: number;
    warn_count: number;
    fail_count: number;
    open_ends: number;
    negative_float: number;
  };
  baseline: DashboardBaselineInfo;
  snapshot_date?: string;
}

export interface DashboardPortfolioSummary {
  mode: 'portfolio';
  projects: { total: number; critical: number; warning: number; ok: number };
  total_overdue_milestones: number;
}

export type DashboardSummary = DashboardProjectSummary | DashboardPortfolioSummary;

export interface DashboardEvent {
  id: string;
  type: 'import' | 'baseline_change' | 'report_run';
  summary: string;
  created_at: string | null;
  metadata: Record<string, unknown>;
}

export interface DashboardAction {
  id: string;
  priority: number;
  title: string;
  description: string;
  link: string;
  severity: 'critical' | 'warning' | 'info';
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

async function fetchJson<T>(url: string): Promise<T> {
  // Use same auth flow as rest of app: 401 triggers single-flight refresh and retry.
  const res = await authFetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`Dashboard API ${res.status}: ${res.statusText}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function getDashboardSummary(
  projectId?: string | null,
): Promise<DashboardSummary> {
  const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  return fetchJson<DashboardSummary>(`${API_BASE_URL}/v1/dashboard/summary${qs}`);
}

export async function getDashboardEvents(
  projectId?: string | null,
  limit = 5,
): Promise<DashboardEvent[]> {
  const params = new URLSearchParams();
  if (projectId) params.set('project_id', projectId);
  params.set('limit', String(limit));
  const data = await fetchJson<{ events: DashboardEvent[] }>(
    `${API_BASE_URL}/v1/dashboard/events?${params}`,
  );
  return data.events;
}

/**
 * Legacy compat – the old useDashboardData hook imported `dashboardApi.getDashboardData`.
 * Provide a stub so the hook compiles; the new Dashboard page no longer uses it.
 */
export const dashboardApi = {
  async getDashboardData(_role?: string, _projectId?: string) {
    return { kpis: [], charts: {}, nextActions: [] };
  },
};

export async function getDashboardActions(
  projectId?: string | null,
): Promise<DashboardAction[]> {
  const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
  const data = await fetchJson<{ actions: DashboardAction[] }>(
    `${API_BASE_URL}/v1/dashboard/actions${qs}`,
  );
  return data.actions;
}
